import React, { useMemo } from "react";
import { Card, CardBody, CardHeader, Col, Row, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import * as moment from "moment";

import { ActivityReportData } from "./types";
import {
  AXIS_LABEL_STYLE,
  cleanName,
  formatBucket,
  themeColor,
} from "./format";
import FigureRow, { FigureCell } from "./Figures";

const CHART_ID = "activity-report-searches";

/** All three panels share one height so the card never grows to the longest
 * table and leaves the chart floating in white space. */
const PANEL_HEIGHT = 260;

/** Leaderboards are top-N lists; more rows than this belong in Activity Logs. */
const MAX_ROWS = 6;

const PanelHeading = ({ children }: { children: React.ReactNode }) => (
  <h5 className="fs-13 fw-semibold mb-2" style={{ lineHeight: 1.6 }}>
    {children}
  </h5>
);

/**
 * A table panel fixed to the chart's height, scrolling internally rather than
 * stretching the row it sits in.
 *
 * Deliberately not `.table-card`: that class carries a -16px margin on every
 * side so a table can bleed to the edges of a card body. Here the table sits
 * under a heading and beside a sibling column, where those margins pull the
 * header band up over the heading and out into the gutter.
 */
const Panel = ({ children }: { children: React.ReactNode }) => (
  <div
    className="table-responsive border rounded"
    style={{ height: PANEL_HEIGHT, overflowY: "auto" }}
  >
    {children}
  </div>
);

/** Header cells stay put while the rows scroll under them, which needs an
 * opaque background of their own. */
const stickyHeadCell: React.CSSProperties = {
  position: "sticky",
  top: 0,
  zIndex: 1,
  backgroundColor: "var(--vz-light)",
  whiteSpace: "nowrap",
};

const EmptyRow = ({ children }: { children: React.ReactNode }) => (
  <tr>
    <td colSpan={2} className="text-center text-ink-muted py-4 px-3 fs-13">
      {children}
    </td>
  </tr>
);

/**
 * Due diligence searches, which are recorded as their own reports rather than
 * as page views. The date, agency and user filters apply; the page filter
 * cannot, since a search is not a page.
 */
const DueDiligenceSearches = ({
  data,
  granularity,
  comparable,
  pageFiltered,
  loading,
}: {
  data: ActivityReportData | null;
  granularity: string;
  comparable: boolean;
  pageFiltered: boolean;
  loading: boolean;
}) => {
  const searches = data?.searches;
  const points = useMemo(() => searches?.points || [], [searches]);

  const categories = useMemo(
    () => points.map((point) => formatBucket(point.date, granularity)),
    [points, granularity]
  );

  const series = useMemo(
    () => [{ name: "Searches", data: points.map((point) => point.searches) }],
    [points]
  );

  const peak = useMemo(
    () => points.reduce((max, point) => Math.max(max, point.searches), 0),
    [points]
  );

  const topSearchers = useMemo(
    () => (searches?.topSearchers || []).slice(0, MAX_ROWS),
    [searches]
  );
  const topTerms = useMemo(
    () => (searches?.topTerms || []).slice(0, MAX_ROWS),
    [searches]
  );

  // Searches are counts per period, so bars: no gradient fill, no curve
  // inventing values between buckets.
  const options: any = {
    chart: {
      height: PANEL_HEIGHT,
      type: "bar",
      zoom: { enabled: false },
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: [themeColor("--vz-primary", "#3577f1")],
    dataLabels: { enabled: false },
    plotOptions: { bar: { columnWidth: "60%", borderRadius: 2 } },
    xaxis: {
      categories,
      tickAmount: Math.min(Math.max(categories.length - 1, 1), 8),
      labels: {
        rotate: -45,
        rotateAlways: false,
        hideOverlappingLabels: true,
        style: AXIS_LABEL_STYLE,
      },
      tooltip: { enabled: false },
    },
    // Searches are whole numbers: without capping the ticks Apex repeats the
    // same rounded label on every gridline of a short axis.
    yaxis: {
      min: 0,
      forceNiceScale: peak > 0,
      tickAmount: Math.min(Math.max(peak, 1), 4),
      decimalsInFloat: 0,
      labels: {
        formatter: (value: number) => String(Math.round(value)),
        style: {
          ...AXIS_LABEL_STYLE,
          colors: themeColor("--cims-ink-muted", "#686a76"),
        },
      },
    },
    grid: { padding: { left: 8, right: 8 } },
  };

  const current = searches?.current;
  const change = searches?.change;
  const total = current?.total || 0;
  const unattributed = current?.unattributed || 0;
  // Every search in the period was recorded without a user: who searched, and
  // how often each person did, simply cannot be known.
  const noneAttributed = total > 0 && unattributed >= total;

  return (
    <Col xxl={12}>
      <Card className="card-height-100">
        <CardHeader className="align-items-center d-flex flex-wrap gap-2">
          <div className="flex-grow-1">
            <h4 className="card-title mb-0">Due diligence searches</h4>
            {pageFiltered ? (
              <small className="text-ink-muted d-block">
                The page filter doesn&apos;t apply here: a search isn&apos;t a
                page.
              </small>
            ) : null}
            {!loading && unattributed > 0 ? (
              <small className="text-ink-muted d-block">
                {noneAttributed
                  ? "None of these searches were linked to a user, so they count toward the total only."
                  : `${unattributed} of ${total} searches weren't linked to a user, so they count toward the total only.`}
              </small>
            ) : null}
          </div>
          {!loading && searches?.lastSearchAt ? (
            <span
              className="text-ink-muted fs-12"
              title={moment(new Date(searches.lastSearchAt)).format(
                "DD MMM YYYY, HH:mm"
              )}
            >
              Latest search on record:{" "}
              {moment(new Date(searches.lastSearchAt)).fromNow()}
            </span>
          ) : null}
          {!loading && searches && !noneAttributed ? (
            <span className="badge bg-info-subtle text-ink-info fs-12">
              {searches.searcherShare}% of active users searched
            </span>
          ) : null}
        </CardHeader>

        <div className="border-bottom">
          <FigureRow>
            <FigureCell
              figure={{
                label: "Searches",
                value: total,
                change: change?.total,
              }}
              comparable={comparable}
              loading={loading}
            />
            {noneAttributed ? (
              <div className="figure-row__cell figure-row__cell--wide">
                <p className="mb-1 fw-semibold fs-13">
                  Who searched isn&apos;t recorded
                </p>
                <p className="text-ink-muted mb-0 fs-12">
                  These searches were saved without a user, so people searching
                  and searches per person can&apos;t be shown.
                </p>
              </div>
            ) : (
              <>
                <FigureCell
                  figure={{
                    label: "People searching",
                    value: current?.searchers || 0,
                    change: change?.searchers,
                  }}
                  comparable={comparable}
                  loading={loading}
                />
                <FigureCell
                  figure={{
                    label: "Searches per person",
                    value: current?.searchesPerSearcher || 0,
                    decimals: 1,
                    change: change?.searchesPerSearcher,
                  }}
                  comparable={comparable}
                  loading={loading}
                />
              </>
            )}
            <FigureCell
              figure={{
                label: "Bulk search reports",
                value: current?.bulkSearches || 0,
                change: change?.bulkSearches,
              }}
              comparable={comparable}
              loading={loading}
            />
          </FigureRow>
        </div>

        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" size="sm" />
            </div>
          ) : total === 0 ? (
            <div className="text-center py-5">
              <p className="mb-1 fw-medium">
                No due diligence searches in this period
              </p>
              <p className="text-ink-muted fs-13 mb-0">
                Try a longer period. Most searches are run by the RMU, so choose
                &quot;All agencies&quot; to include them.
              </p>
            </div>
          ) : (
            <Row className="g-4">
              <Col xl={6}>
                <PanelHeading>Searches over time</PanelHeading>
                {peak === 0 ? (
                  <div
                    className="d-flex align-items-center justify-content-center text-ink-muted fs-13 border border-dashed rounded"
                    style={{ height: PANEL_HEIGHT }}
                  >
                    No searches in this period.
                  </div>
                ) : (
                  <div
                    style={{ height: PANEL_HEIGHT }}
                    role="img"
                    aria-label={`Bar chart of due diligence searches by ${granularity}: ${total.toLocaleString()} in total, at most ${peak} in one ${granularity}.`}
                  >
                    <ReactApexChart
                      dir="ltr"
                      options={options}
                      series={series}
                      id={CHART_ID}
                      type="bar"
                      height={PANEL_HEIGHT}
                      className="apex-charts"
                    />
                  </div>
                )}
              </Col>

              <Col xl={3} md={6}>
                <PanelHeading>Top searchers</PanelHeading>
                <Panel>
                  <table className="table table-sm align-middle table-borderless mb-0">
                    <thead className="text-ink-muted">
                      <tr>
                        <th scope="col" style={stickyHeadCell}>
                          User
                        </th>
                        <th
                          scope="col"
                          className="text-end"
                          style={stickyHeadCell}
                        >
                          Searches
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topSearchers.length === 0 ? (
                        <EmptyRow>
                          {noneAttributed
                            ? "Searches in this period weren't linked to a user."
                            : "No searches by a known user in this period."}
                        </EmptyRow>
                      ) : (
                        topSearchers.map((row) => (
                          <tr key={row.userId}>
                            <td>
                              <div
                                className="fw-medium text-truncate"
                                title={cleanName(row.name)}
                                style={{ maxWidth: "160px" }}
                              >
                                {cleanName(row.name)}
                              </div>
                              {row.agencyName ? (
                                <small className="text-ink-muted">
                                  {row.agencyName}
                                </small>
                              ) : null}
                            </td>
                            <td className="text-end fw-semibold">
                              {row.searches}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Panel>
              </Col>

              <Col xl={3} md={6}>
                <PanelHeading>Most searched terms</PanelHeading>
                <Panel>
                  <table
                    className="table table-sm align-middle table-borderless mb-0"
                    style={{ tableLayout: "fixed" }}
                  >
                    <thead className="text-ink-muted">
                      <tr>
                        <th scope="col" style={stickyHeadCell}>
                          Term
                        </th>
                        <th
                          scope="col"
                          className="text-end"
                          style={{ ...stickyHeadCell, width: "6.5rem" }}
                        >
                          Searches
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {topTerms.length === 0 ? (
                        <EmptyRow>No search terms were recorded.</EmptyRow>
                      ) : (
                        topTerms.map((row, index) => (
                          <tr key={`${row.term}-${index}`}>
                            <td>
                              {/* Company names run long; one line each keeps the
                                  rows level with the searchers beside them. */}
                              <div
                                className="fw-medium text-truncate"
                                title={row.term}
                              >
                                {row.term}
                              </div>
                            </td>
                            <td className="text-end fw-semibold">
                              {row.searches}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </Panel>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

export default DueDiligenceSearches;
